import {
  AddressSourceEnum,
  IInvoiceRespSchema,
  ILegalEntityResponseSchema,
  IOfferingRes,
  IOneTimeInvoiceFormSchemaWithOfferings,
  NetTermsEnum,
  OneTimeInvoiceRequestSchema,
} from '@monetize/types/app';
import { formatISO } from 'date-fns/formatISO';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useGetAccountById } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetAllBillGroups } from '../../../api/billGroupService';
import { useCreateOneTimeInvoice } from '../../../api/invoiceService';
import { doGetOfferingById } from '../../../api/productCatalogService';
import { useGetById } from '../../../api/queryUtils';
import { useGetInvoiceSettings } from '../../../api/settingsService';
import {
  MButton,
  MLink,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import {
  getAccountInvoicesRoute,
  getInvoiceDetailRoute,
} from '../../../constants/routes';
import { useFlags } from '../../../services/launchDarkly';
import { logger } from '../../../services/logger';
import { useToast } from '../../../services/toast';
import { OneTimeInvoiceFormBody } from './components/OneTimeInvoiceFormBody';
import { OneTimeInvoiceProvider } from './components/oneTimeInvoiceContext';
import {
  getBillGroupNetTerms,
  getBillGroupReferenceNumbers,
  getDefaultInvoiceTerms,
  getInvoiceContactId,
  isOneTimeInvoiceProductSelected,
  mapInvoiceItemsToOfferings,
  REFERENCE_NUMBER_FIELDS,
} from './oneTimeInvoice.utils';

export const OneTimeInvoiceForm = () => {
  const navigate = useNavigate();
  // TODO: handle case where accountId is missing - probably error state
  const { accountId = '' } = useParams();

  const [searchParams] = useSearchParams();
  const sourceInvoiceId = searchParams.get('sourceInvoiceId') || '';
  const isDuplicate = !!sourceInvoiceId;

  const { addToast } = useToast();
  const { enableLegalEntityInvoiceTerms } = useFlags();

  const today = useMemo(
    () => formatISO(new Date(), { representation: 'date' }),
    [],
  );

  const {
    mutateAsync: createOneTimeInvoice,
    isPending: isCreatingOneTimeInvoice,
  } = useCreateOneTimeInvoice();

  const { data: account, isLoading: accountLoading } = useGetAccountById(
    accountId,
    {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
    },
  );

  const { data: billGroupsData, isLoading: billGroupsLoading } =
    useGetAllBillGroups(
      { filters: { accountId } },
      { enabled: !!accountId, refetchOnWindowFocus: false },
    );

  const { data: sourceInvoice, isError: isSourceInvoiceError } =
    useGetById<IInvoiceRespSchema>('invoices', sourceInvoiceId, {
      enabled: isDuplicate,
      refetchOnWindowFocus: false,
      meta: { showErrorToast: true },
    });

  const formMethods = useForm<IOneTimeInvoiceFormSchemaWithOfferings>({
    defaultValues: {
      accountId,
      billGroupId: '',
      currency: '',
      legalEntityId: '',
      addressInformation: {
        addressSource: AddressSourceEnum.CONTACT,
        billingAddressId: null,
        shippingAddressId: null,
      },
      billingContactId: '',
      shippingContactId: '',
      netTerms: undefined,
      purchaseOrderNumber: '',
      vatNumber: '',
      registrationNumber: '',
      invoicingDate: today,
      dueDate: today,
      invoiceTerms: '',
      memo: '',
      customFields: {},
    },
  });
  const { handleSubmit, watch, setValue, getValues } = formMethods;

  // Account-level values that are read-only on the form. Derived directly from
  // the loaded account (or source invoice when duplicating) so they are
  // available for both display and the create payload.
  const derivedCurrency =
    sourceInvoice?.currency ?? account?.defaultCurrency ?? '';
  const derivedLegalEntityId =
    sourceInvoice?.fromCompany.legalEntityId ??
    account?.defaultLegalEntityId ??
    '';
  const accountName =
    sourceInvoice?.account.accountName ?? account?.accountName ?? '';
  const currency = derivedCurrency || 'USD';

  useEffect(() => {
    if (derivedCurrency) {
      setValue('currency', derivedCurrency);
    }
    if (derivedLegalEntityId) {
      setValue('legalEntityId', derivedLegalEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedCurrency, derivedLegalEntityId]);

  // When duplicating, copy the source invoice's header fields and line items
  // (selection, quantity, and price) onto the form once it is loaded.
  const [isSourceInvoiceHydrated, setIsSourceInvoiceHydrated] = useState(false);
  const sourceInvoiceHydrationRef = useRef(false);
  const isHydratingFromSourceInvoice =
    isDuplicate && !isSourceInvoiceHydrated && !isSourceInvoiceError;

  useEffect(() => {
    if (!sourceInvoice || sourceInvoiceHydrationRef.current) {
      return;
    }
    sourceInvoiceHydrationRef.current = true;

    // Header fields carried over from the source invoice. These take
    // precedence over bill-group backfill (which only fills empty fields).
    setValue('billGroupId', sourceInvoice.billGroupId ?? '', {
      shouldValidate: true,
    });
    if (sourceInvoice.netTerms) {
      setValue('netTerms', sourceInvoice.netTerms as NetTermsEnum);
    }
    setValue('purchaseOrderNumber', sourceInvoice.purchaseOrderNumber ?? '');
    setValue('vatNumber', sourceInvoice.vatNumber ?? '');
    setValue('registrationNumber', sourceInvoice.registrationNumber ?? '');
    setValue('invoiceTerms', sourceInvoice.invoiceTerms ?? '');
    setValue('memo', sourceInvoice.invoiceMemo ?? '');
    const billingContactId = getInvoiceContactId(sourceInvoice.billingContact);
    const shippingContactId = getInvoiceContactId(
      sourceInvoice.shippingContact,
    );
    if (billingContactId) {
      setValue('billingContactId', billingContactId, { shouldValidate: true });
    }
    if (shippingContactId) {
      setValue('shippingContactId', shippingContactId, {
        shouldValidate: true,
      });
    }

    const hydrateOfferingsFromSourceInvoice = async () => {
      try {
        const offeringIds = [
          ...new Set(
            sourceInvoice.invoiceItems.map(({ offeringId }) => offeringId),
          ),
        ];
        const results = await Promise.allSettled(
          offeringIds.map((offeringId) => doGetOfferingById(offeringId)),
        );
        const offeringsById = results.reduce<Record<string, IOfferingRes>>(
          (acc, result) => {
            if (result.status === 'fulfilled') {
              acc[result.value.id] = result.value;
            }
            return acc;
          },
          {},
        );
        const { offerings, skippedItems } = mapInvoiceItemsToOfferings({
          invoiceItems: sourceInvoice.invoiceItems,
          offeringsById,
        });
        setValue('offerings', offerings, { shouldValidate: true });
        if (skippedItems.length > 0) {
          addToast({
            severity: 'warning',
            summary: 'Some items were not carried over',
            detail: `The following items could not be copied because their offering or product is no longer available: ${skippedItems.join(
              ', ',
            )}`,
          });
        }
      } catch (ex) {
        logger.error('Error while copying items from source invoice', ex);
        handleApiErrorToast(
          new Error(
            'There was an error copying line items from the source invoice.',
          ),
        );
      } finally {
        setIsSourceInvoiceHydrated(true);
      }
    };
    hydrateOfferingsFromSourceInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceInvoice]);

  const selectedBillGroupId = watch('billGroupId');

  // Once bill groups load, reconcile the selected bill group:
  // - clear a prefilled id (e.g. from a duplicated invoice) that no longer
  //   exists so the form stays invalid instead of leaving a dead Submit button
  // - auto-select the only bill group when the account has exactly one
  useEffect(() => {
    if (!billGroupsData) {
      return;
    }
    const currentBillGroupId = getValues('billGroupId');
    if (
      currentBillGroupId &&
      !billGroupsData.some(({ id }) => id === currentBillGroupId)
    ) {
      setValue('billGroupId', '', { shouldValidate: true });
      return;
    }
    if (
      !currentBillGroupId &&
      billGroupsData.length === 1 &&
      billGroupsData[0].id
    ) {
      setValue('billGroupId', billGroupsData[0].id, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billGroupsData]);

  // Derive header defaults from the selected bill group. Runs once per distinct
  // selection so subsequent re-renders (or refetches) don't clobber edits. Only
  // empty fields are filled, so source-invoice values set above take precedence.
  const derivedBillGroupIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const billGroup = billGroupsData?.find(
      ({ id }) => id === selectedBillGroupId,
    );
    if (!billGroup || derivedBillGroupIdRef.current === selectedBillGroupId) {
      return;
    }
    const isInitialSelection = derivedBillGroupIdRef.current === undefined;
    derivedBillGroupIdRef.current = selectedBillGroupId;

    const values = getValues();
    const netTerms = getBillGroupNetTerms({
      billGroupNetTerms: billGroup.netTerms,
      currentNetTerms: values.netTerms,
      isInitialSelection,
    });
    if (netTerms) {
      setValue('netTerms', netTerms, { shouldValidate: true });
    }
    // Reference numbers track the selected bill group, so switching to one
    // without a PO or VAT number clears the previous bill group's value.
    const referenceNumbers = getBillGroupReferenceNumbers({
      billGroup,
      currentValues: values,
      isInitialSelection,
    });
    REFERENCE_NUMBER_FIELDS.forEach((field) => {
      if (field in referenceNumbers) {
        setValue(field, referenceNumbers[field]);
      }
    });
    setValue(
      'addressInformation',
      {
        // FIXME: bill group addressSource is a different enum than the form's
        addressSource: billGroup.addressSource as unknown as AddressSourceEnum,
        billingAddressId:
          values.addressInformation?.billingAddressId ??
          billGroup.billingAddress?.id ??
          null,
        shippingAddressId:
          values.addressInformation?.shippingAddressId ??
          billGroup.shippingAddress?.id ??
          null,
      },
      { shouldValidate: true },
    );
    if (!values.billingContactId && billGroup.billingContact?.id) {
      setValue('billingContactId', billGroup.billingContact.id, {
        shouldValidate: true,
      });
    }
    if (!values.shippingContactId && billGroup.shippingContact?.id) {
      setValue('shippingContactId', billGroup.shippingContact.id, {
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBillGroupId, billGroupsData]);

  const {
    data: legalEntity,
    isLoading: legalEntityLoading,
    isError: isLegalEntityError,
  } = useGetById<ILegalEntityResponseSchema>(
    'legalEntities',
    derivedLegalEntityId,
    {
      enabled: !!derivedLegalEntityId,
      refetchOnWindowFocus: false,
    },
  );

  const { data: invoiceSettings, isLoading: invoiceSettingsLoading } =
    useGetInvoiceSettings({
      enabled: !isDuplicate,
      refetchOnWindowFocus: false,
    });

  // Invoice terms default from the legal entity configured for the invoice
  // currency, then from the tenant default, and stay blank when neither is
  // configured. Only new invoices are defaulted: a duplicate carries its source
  // invoice's terms, including terms the user deliberately emptied there. The
  // ref keeps this to a single pass so a later refetch cannot overwrite an edit.
  const hasDefaultedInvoiceTermsRef = useRef(false);
  useEffect(() => {
    // The legal entity id comes from the account, so wait for the account, and
    // then for the legal entity itself whenever the account names one. A failed
    // legal entity fetch falls through to the tenant default rather than
    // leaving the field blank forever.
    const isLegalEntityPending =
      !!derivedLegalEntityId && !legalEntity && !isLegalEntityError;
    if (
      isDuplicate ||
      hasDefaultedInvoiceTermsRef.current ||
      accountLoading ||
      invoiceSettingsLoading ||
      isLegalEntityPending
    ) {
      return;
    }
    hasDefaultedInvoiceTermsRef.current = true;

    const defaultInvoiceTerms = getDefaultInvoiceTerms({
      legalEntity,
      currency: derivedCurrency,
      invoiceTermsSettings: invoiceSettings?.invoiceTerms,
      enableLegalEntityInvoiceTerms,
    });
    if (defaultInvoiceTerms && !getValues('invoiceTerms')) {
      setValue('invoiceTerms', defaultInvoiceTerms);
    }
  }, [
    accountLoading,
    derivedCurrency,
    derivedLegalEntityId,
    enableLegalEntityInvoiceTerms,
    getValues,
    invoiceSettings,
    invoiceSettingsLoading,
    isDuplicate,
    isLegalEntityError,
    legalEntity,
    setValue,
  ]);

  useEffect(() => {
    // TODO: should show toast error message
    if (!accountId) {
      navigate(-1);
    }
  }, [accountId, navigate]);

  async function handleSave(data: IOneTimeInvoiceFormSchemaWithOfferings) {
    try {
      let i = 1;
      const invoiceItems = data.offerings.flatMap(
        ({ offeringId, offeringDescription, startDate, endDate, products }) =>
          products.filter(isOneTimeInvoiceProductSelected).map((product) => ({
            offeringId,
            offeringDescription,
            startDate,
            endDate: endDate ?? startDate,
            position: i++,
            ...product,
          })),
      );

      if (invoiceItems.length === 0) {
        handleApiErrorToast(
          new Error(
            'Please add at least one line item before creating the invoice.',
          ),
        );
        return;
      }

      const invoice = OneTimeInvoiceRequestSchema.parse({
        ...data,
        invoiceItems,
      });

      const createdInvoice = await createOneTimeInvoice({
        billGroupId: data.billGroupId,
        invoice,
      });
      navigate(getInvoiceDetailRoute(createdInvoice.id));
    } catch (ex) {
      handleApiErrorToast(ex);
      logger.error('Error while creating one time invoice', ex);
    }
  }

  function handleError(err: unknown) {
    console.warn('Error saving invoice', err);
  }

  if (
    accountLoading ||
    billGroupsLoading ||
    legalEntityLoading ||
    invoiceSettingsLoading ||
    isHydratingFromSourceInvoice
  ) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer data-testid="one-time-invoice-form">
      <FormProvider {...formMethods}>
        <OneTimeInvoiceProvider
          accountId={accountId}
          accountName={accountName}
          legalEntityName={legalEntity?.name || ''}
          addressFormat={legalEntity?.addressFormat}
          currency={currency}
          billGroups={billGroupsData || []}
          isBillGroupsLoading={billGroupsLoading}
        >
          <form
            style={{ width: '100%' }}
            onSubmit={handleSubmit(handleSave, handleError)}
          >
            <MPageHeader
              hasBackButton
              title="Create Invoice"
              status="Draft"
              parentLink={getAccountInvoicesRoute(accountId)}
              parentLinkTitle={accountName}
              subTitle={
                <MText>
                  This is a onetime invoice. Changes made here will not affect
                  any default settings within this Bill Group.
                  <MLink
                    pl={0.5}
                    href="https://docs.monetizenow.io/docs/one-time-invoices"
                    target="_blank"
                    textDecoration="underline"
                  >
                    Learn more
                  </MLink>
                </MText>
              }
              childrenProps={{ alignSelf: 'flex-start' }}
            >
              <MButton
                type="submit"
                variant="primary"
                isLoading={isCreatingOneTimeInvoice}
                isDisabled={!selectedBillGroupId || isCreatingOneTimeInvoice}
              >
                Submit
              </MButton>
            </MPageHeader>

            <OneTimeInvoiceFormBody />
          </form>
        </OneTimeInvoiceProvider>
      </FormProvider>
    </MPageContainer>
  );
};
